// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Bid reconciliation: "attribution implies alignment."
//
// An estimate line can be credited to a partner (sourceBidId, or inferred
// from tradeName + tradeDate) while that partner's bid line was never aligned
// — so the budget column shows "Ridgeline" but the partner's comparison
// column shows "—" and the bid sits in Unassigned. Two contradictory signals.
//
// This heals that: for every attributed estimate line, find the credited bid
// and align its matching item (exact normalized description) to the line, so
// the chip and the comparison always agree. Pure; returns updated alignments
// only when something changed (null otherwise) so callers can skip a no-op
// state update.

import type { Bid, BidPartner } from "../projectData";

type AnyItemState = Record<string, {
  description?: string;
  sourceBidId?: string;
  tradeName?: string;
  tradeDate?: string;
}>;

type Alignments = Record<string, Record<string, string>>;

function normDesc(s: string): string {
  return (s || "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function reconcileAttributionAlignments(args: {
  bids: Bid[];
  bidPartners: BidPartner[];
  itemState: AnyItemState;
  bidAlignments: Alignments;
}): Alignments | null {
  const { bids, bidPartners, itemState, bidAlignments } = args;
  if (!bids.length) return null;

  const bidById = new Map(bids.map((b) => [b.id, b]));
  const partnerNameById = new Map(bidPartners.map((p) => [p.id, p.name]));

  // Resolve the bid an estimate item is credited to: explicit link first,
  // then inferred from tradeName + tradeDate (matches the chip's own logic).
  const resolveBid = (li: AnyItemState[string]): Bid | null => {
    if (li.sourceBidId && bidById.has(li.sourceBidId)) return bidById.get(li.sourceBidId)!;
    if (li.tradeName && li.tradeDate) {
      return bids.find((b) => partnerNameById.get(b.partnerId) === li.tradeName && b.date === li.tradeDate) || null;
    }
    return null;
  };

  // Work on a shallow per-scope copy so we don't mutate the input.
  const next: Alignments = {};
  for (const k of Object.keys(bidAlignments)) next[k] = { ...bidAlignments[k] };
  let changed = false;

  for (const [estItemId, li] of Object.entries(itemState)) {
    if (!li) continue;
    const bid = resolveBid(li);
    if (!bid) continue;
    const scopeMap = (next[bid.scopeId] ||= {});
    const target = normDesc(li.description || "");
    if (!target) continue;
    for (const bi of bid.items || []) {
      // Skip bid items already aligned anywhere; align the matching one here.
      if (scopeMap[bi.id]) continue;
      if (normDesc(bi.description) === target) {
        scopeMap[bi.id] = estItemId;
        changed = true;
      }
    }
  }

  return changed ? next : null;
}
